import React, { useEffect, useState } from "react";
import { Newspaper, ExternalLink, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * Compact horizontal "mood ribbon" — last N news articles' sentiment as
 * color-coded dots in chronological order (oldest left → newest right).
 * Hover any dot for a tooltip; clicking opens the article.
 */
const MOOD_COLORS = {
  Bullish: "#3F7A56",
  "Slightly Bullish": "#7BC79E",
  Neutral: "#9C9F92",
  "Slightly Bearish": "#E89B8B",
  Bearish: "#C66B59",
  "No coverage": "#9C9F92",
};

function dotColor(score) {
  if (score >= 0.20) return "#3F7A56"; // strong bull
  if (score >= 0.05) return "#7BC79E"; // mild bull
  if (score <= -0.20) return "#C66B59"; // strong bear
  if (score <= -0.05) return "#E89B8B"; // mild bear
  return "#9C9F92"; // neutral
}

function fmtTime(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return s;
  }
}

export default function NewsMoodRibbon({ assetType, symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!symbol || !assetType) return;
    let cancel = false;
    setLoading(true);
    api
      .get(`/news/mood/${assetType}/${symbol}?limit=12`)
      .then((r) => !cancel && setData(r.data))
      .catch(() => !cancel && setData(null))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [assetType, symbol]);

  if (loading) {
    return (
      <div className="rounded-xl bg-white border border-[#EAE6E1] px-4 py-2 flex items-center gap-2 text-xs text-[#6B7068]" data-testid="news-mood-ribbon-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading news mood...
      </div>
    );
  }

  if (!data || data.count === 0) {
    return null;
  }

  // Chronological order (oldest left → newest right) for proper time-flow reading
  const items = [...data.items].reverse();
  const moodColor = MOOD_COLORS[data.mood] || "#9C9F92";

  return (
    <div
      className="rounded-xl bg-white border border-[#EAE6E1] px-4 py-3 flex items-center gap-4 flex-wrap"
      data-testid="news-mood-ribbon"
    >
      <div className="flex items-center gap-2 shrink-0">
        <Newspaper className="w-4 h-4 text-[#6B7068]" strokeWidth={1.75} />
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#6B7068] leading-none">
            News mood
          </div>
          <div
            className="text-sm font-mono-ticker font-semibold leading-tight"
            style={{ color: moodColor }}
            data-testid="news-mood-label"
          >
            {data.mood}
            <span className="text-[#6B7068] font-normal text-[11px] ml-1">
              · {data.count} articles
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-[200px] flex items-center gap-1.5 overflow-x-auto">
        {items.map((it, i) => (
          <a
            key={i}
            href={it.url || "#"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`news-mood-dot-${i}`}
            className="group relative shrink-0 inline-flex items-center justify-center"
            title={`${it.title}\n— ${it.source} · ${fmtTime(it.time_published)} · ${it.label || ""} (${it.score >= 0 ? "+" : ""}${it.score.toFixed(2)})`}
          >
            <span
              className="block rounded-full transition-soft group-hover:scale-150 group-hover:ring-2 group-hover:ring-[#2A2F2B]/10"
              style={{
                width: 12,
                height: 12,
                background: dotColor(it.score),
                opacity: 0.6 + Math.min(Math.abs(it.score), 1) * 0.4,
              }}
            />
            <span className="hidden group-hover:block absolute z-20 bottom-full mb-2 left-1/2 -translate-x-1/2 w-64 max-w-xs bg-[#2A2F2B] text-white text-[11px] rounded-lg p-2.5 shadow-xl pointer-events-none">
              <span className="block font-semibold mb-1 line-clamp-2 leading-tight">{it.title}</span>
              <span className="block text-[10px] opacity-70">
                {it.source} · {fmtTime(it.time_published)}
              </span>
              <span
                className="block text-[10px] mt-1"
                style={{ color: dotColor(it.score) }}
              >
                {it.label} ({it.score >= 0 ? "+" : ""}
                {it.score.toFixed(2)})
              </span>
            </span>
          </a>
        ))}
      </div>

      <div className="hidden md:flex items-center gap-1 text-[10px] text-[#6B7068] shrink-0">
        <span>Older</span>
        <ExternalLink className="w-2.5 h-2.5 mx-1" />
        <span>Newest</span>
      </div>
    </div>
  );
}
